/* eslint-disable @typescript-eslint/no-explicit-any */
import { Reporter } from 'src/api';
import {
  AssociatedCasePermissionsType,
  AssociatedReportPermissionsType,
  CategoryDisplayType,
  Maybe,
  UserType,
} from 'src/graphql';
import { FormState, MiReportSourceForDisplay } from 'src/types';
import { returnDashIfNoValue } from 'src/utils';
import { hiddenDate, hiddenDots, hiddenFormInput } from './hiddenDots';

export const getSubmissionDate = (
  createdAt: any,
  submittedAt?: any,
  permission?: Maybe<boolean>,
) =>
  permission
    ? submittedAt
      ? submittedAt?.split('T')[0]
      : createdAt.split('T')[0]
    : hiddenDate;

export const getInputFormat = (
  input?: Maybe<CategoryDisplayType> | Maybe<UserType>,
  permission?: Maybe<boolean>,
) =>
  permission
    ? {
        label: input && input?.name ? input.name : '',
        value: input && input?.id ? input.id : '',
      }
    : hiddenFormInput;

export const getSubstantiationStatus = (
  substantiation?: Maybe<string>,
  permission?: Maybe<boolean>,
) =>
  permission
    ? {
        label: substantiation ?? 'Undetermined',
        value: substantiation ?? 'Undetermined',
      }
    : hiddenFormInput;

export const getMiReportSource = (
  source: MiReportSourceForDisplay | string,
  permission?: Maybe<boolean>,
) => (permission ? source : hiddenDots);

export const getSubmissionRoute = (
  submissionRoute: string,
  permission?: Maybe<boolean>,
) => (permission ? submissionRoute : hiddenDots);

export const getReporterDetails = ({
  reporter,
  isAnonymised,
  reportSource,
  piiPermission,
  reportRelationshipToCompany,
  updateReportRelationshipToOrgEnabled,
}: {
  reporter: Maybe<UserType> | undefined;
  isAnonymised: boolean;
  reportSource: string;
  piiPermission?: Maybe<boolean>;
  reportRelationshipToCompany?: Maybe<string>;
  updateReportRelationshipToOrgEnabled: boolean;
}) => {
  const reporterDetailsHidden = {
    name: hiddenDots,
    eid: hiddenDots,
    email: hiddenDots,
    role: hiddenDots,
    id: Number(hiddenDots),
    relationshipToOrganisation: hiddenDots,
    contactEmail: hiddenDots,
    department: hiddenDots,
    country: hiddenDots,
    site: hiddenDots,
  } as Reporter;

  const reporterRelationship =
    reportRelationshipToCompany ?? reporter?.relationshipToOrganisation;
  const relationshipToOrganisation = returnDashIfNoValue(reporterRelationship);

  const reporterDetailsVisible = {
    name: returnDashIfNoValue(reporter?.name),
    eid: returnDashIfNoValue(reporter?.eid),
    email: returnDashIfNoValue(reporter?.email),
    role: returnDashIfNoValue(reporter?.role),
    id: Number(returnDashIfNoValue(reporter?.id?.toString())),
    relationshipToOrganisation,
    contactEmail: returnDashIfNoValue(reporter?.contactEmail),
    department: returnDashIfNoValue(reporter?.department),
    country: returnDashIfNoValue(reporter?.country),
    site: returnDashIfNoValue(reporter?.site),
  } as Reporter;

  if (
    piiPermission &&
    reportSource === 'open-reporting' &&
    reporter?.id !== 0 // OPR anonymous reporter has id 0
  ) {
    return reporterDetailsVisible;
  }

  if (isAnonymised && !updateReportRelationshipToOrgEnabled) {
    return reporterDetailsHidden;
  }

  if (piiPermission) {
    if (isAnonymised) {
      return { ...reporterDetailsHidden, relationshipToOrganisation };
    }
    return reporterDetailsVisible;
  } else {
    return reporterDetailsHidden;
  }
};

export const getStringFormat = (
  text?: Maybe<string>,
  permission?: Maybe<boolean>,
) => (permission && text ? text : permission ? '' : hiddenDots);

export const getNotificationEmailState = (
  nonEditableNotificationEmail: boolean,
  canEditReportDetails?: Maybe<boolean>,
  canViewReportDetailsPii?: Maybe<boolean>,
  canViewReportDetails?: Maybe<boolean>,
) => {
  const editable = canEditReportDetails && canViewReportDetailsPii;
  const viewable = canViewReportDetails && canViewReportDetailsPii;

  if (editable && !nonEditableNotificationEmail) {
    return FormState.DEFAULT;
  }
  if (viewable || (editable && nonEditableNotificationEmail)) {
    return FormState.READONLY;
  }

  return FormState.DISABLED;
};

export const isDownloadReportEnabled = (
  reportPermissions: AssociatedReportPermissionsType | null,
  casePermissions: AssociatedCasePermissionsType | null,
  messagingDisabled: boolean,
) => {
  if (!reportPermissions || !casePermissions) return false;

  const permissions = Boolean(
    casePermissions?.canViewCaseDetails &&
    casePermissions?.canViewCaseDetailsPii &&
    casePermissions?.canViewCaseWorkspaceTab &&
    casePermissions?.canViewCaseAuditLogsTab &&
    reportPermissions?.canViewReportDetails &&
    (messagingDisabled || reportPermissions?.canViewReportMessages) &&
    reportPermissions?.canViewReportDetailsPii,
  );
  return permissions;
};
